import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HustleBadge, type HustleLevel } from "@/components/HustleBadge";
import { Loader2, Search, Store, Clock, Megaphone } from "lucide-react";
import { SponsoredAdsRail } from "@/components/SponsoredAdsRail";

interface MerchantRow {
  user_id: string;
  display_name: string;
  cashtag: string;
  store_name: string | null;
  avatar_url: string | null;
  hustle_level: HustleLevel | string;
  hustle_score: number;
  business_hours: Record<string, { open: string; close: string; closed: boolean }>;
  category?: string | null;
}

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

const isOpenNow = (hours: MerchantRow["business_hours"]): boolean => {
  if (!hours) return false;
  const now = new Date();
  const day = DAY_KEYS[now.getDay()];
  const slot = hours[day];
  if (!slot || slot.closed) return false;
  const [oh, om] = (slot.open || "00:00").split(":").map(Number);
  const [ch, cm] = (slot.close || "00:00").split(":").map(Number);
  const mins = now.getHours() * 60 + now.getMinutes();
  return mins >= oh * 60 + om && mins <= ch * 60 + cm;
};

const LEVEL_RANK: Record<string, number> = { Black: 5, Platinum: 4, Gold: 3, Silver: 2, Bronze: 1 };

const Discover = () => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState<string>("all");
  const [openOnly, setOpenOnly] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("user_id, display_name, cashtag, store_name, avatar_url, hustle_level, hustle_score, business_hours")
        .eq("is_merchant", true)
        .limit(500);

      const list = (profs as MerchantRow[] | null) ?? [];
      const ids = list.map((m) => m.user_id);

      const catMap: Record<string, string> = {};
      if (ids.length) {
        const { data: apps } = await supabase
          .from("merchant_applications")
          .select("user_id, category, status")
          .in("user_id", ids)
          .eq("status", "approved");
        (apps ?? []).forEach((a: any) => {
          if (a.category) catMap[a.user_id] = a.category;
        });
      }
      setCategories(catMap);
      setMerchants(list.map((m) => ({ ...m, category: catMap[m.user_id] ?? null })));
      setLoading(false);
    })();
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set<string>();
    Object.values(categories).forEach((c) => c && set.add(c));
    return Array.from(set).sort();
  }, [categories]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return merchants
      .filter((m) => {
        if (activeCat !== "all" && (m.category ?? "") !== activeCat) return false;
        if (openOnly && !isOpenNow(m.business_hours)) return false;
        if (!needle) return true;
        return (
          m.display_name?.toLowerCase().includes(needle) ||
          m.cashtag?.toLowerCase().includes(needle) ||
          (m.store_name ?? "").toLowerCase().includes(needle) ||
          (m.category ?? "").toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => {
        const ao = isOpenNow(a.business_hours) ? 1 : 0;
        const bo = isOpenNow(b.business_hours) ? 1 : 0;
        if (ao !== bo) return bo - ao;
        const ar = LEVEL_RANK[a.hustle_level as string] ?? 0;
        const br = LEVEL_RANK[b.hustle_level as string] ?? 0;
        if (ar !== br) return br - ar;
        return (b.hustle_score ?? 0) - (a.hustle_score ?? 0);
      });
  }, [merchants, q, activeCat, openOnly]);

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Discover vendors</h1>
        <p className="text-center text-xs opacity-80 mt-1">Find merchants on Cash Isle</p>
      </BrandHeader>

      <div className="px-5 -mt-5">
        <div className="bg-background rounded-2xl shadow-card p-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendors, cashtag, category"
              className="pl-9 h-11 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
            <Chip active={activeCat === "all"} onClick={() => setActiveCat("all")}>All</Chip>
            {allCategories.map((c) => (
              <Chip key={c} active={activeCat === c} onClick={() => setActiveCat(c)}>
                {c}
              </Chip>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setOpenOnly((v) => !v)}
              className={`text-xs font-medium inline-flex items-center gap-1 px-3 py-1.5 rounded-full border transition-smooth ${
                openOnly
                  ? "bg-success/15 text-success border-success/30"
                  : "bg-muted text-muted-foreground border-transparent"
              }`}
            >
              <Clock className="h-3 w-3" /> Open now
            </button>
            <span className="text-xs text-muted-foreground">{filtered.length} result{filtered.length === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      <SponsoredAdsRail />

      <section className="px-5 mt-5 flex-1 pb-8">
        {loading ? (
          <div className="py-16 grid place-items-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
            No vendors match your search.
          </div>
        ) : (
          <ul className="space-y-3">
            {filtered.map((m) => {
              const open = isOpenNow(m.business_hours);
              return (
                <li key={m.user_id}>
                  <Link
                    to={`/pay/${m.cashtag}`}
                    className="flex items-center gap-3 bg-card border border-border rounded-2xl p-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center text-primary overflow-hidden shrink-0">
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt={m.display_name} className="h-full w-full object-cover" />
                      ) : (
                        <Store className="h-6 w-6" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-display font-bold truncate">
                          {m.store_name || m.display_name}
                        </p>
                        {m.hustle_level && (
                          <HustleBadge level={m.hustle_level as HustleLevel} score={m.hustle_score} />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        ${m.cashtag}{m.category ? ` · ${m.category}` : ""}
                      </p>
                    </div>
                    <span
                      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        open ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {open ? "Open" : "Closed"}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-6 text-center space-y-2">
          <Button variant="ghost" asChild className="w-full">
            <Link to="/ads"><Megaphone className="h-4 w-4 mr-1" /> Promote your business</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/merchant/apply">Are you a vendor? Apply →</Link>
          </Button>
        </div>
      </section>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition-smooth ${
      active
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-muted text-foreground/80 border-transparent hover:bg-muted/70"
    }`}
  >
    {children}
  </button>
);

export default Discover;
